/**
 * useCommunityInvites Hook
 * Handles community invite actions using React Query
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  acceptInvite,
  cancelInvite,
  getEligibleUsersForInvite,
  getPendingInvites,
  inviteUsers,
  rejectInvite,
  resendInvite,
} from "../services/community.service"

export function useInviteUsers(communityId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userIds, roleType }: { userIds: string[]; roleType: "ritualist" | "member" }) =>
      inviteUsers(communityId, userIds, roleType),
    onSuccess: () => {
      toast.success("Invites sent successfully")
      // Invalidate all community members queries (both regular and infinite)
      queryClient.invalidateQueries({ queryKey: ["community-members", communityId] })
      queryClient.invalidateQueries({ queryKey: ["communityMembers", communityId] })
      queryClient.invalidateQueries({ queryKey: ["communityRitualists", communityId] })
      queryClient.invalidateQueries({ queryKey: ["pending-invites", communityId] })
      queryClient.invalidateQueries({ queryKey: ["eligible-users", communityId] })
      queryClient.invalidateQueries({ queryKey: ["profileStatistics"] })
      queryClient.invalidateQueries({ queryKey: ["publicProfileStatistics"] })
    },
    onError: () => {
      toast.error("Failed to send invites")
    },
  })
}

export function usePendingInvites(communityId: string, enabled = true) {
  return useQuery({
    queryKey: ["pending-invites", communityId],
    queryFn: () => getPendingInvites(communityId),
    enabled,
    staleTime: 1000 * 60 * 2, // 2 minutes
  })
}

export function useEligibleUsers(
  communityId: string,
  roleType: "ritualist" | "member" = "member",
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  return useInfiniteQuery({
    queryKey: ["eligible-users", communityId, roleType],
    queryFn: ({ pageParam = 1 }) => getEligibleUsersForInvite(communityId, roleType, pageParam),
    initialPageParam: 1,
    getNextPageParam: lastPage => (lastPage.hasMore ? lastPage.nextPage : undefined),
    enabled: !!communityId && enabled,
    staleTime: 1000 * 60 * 2, // 2 minutes
  })
}

export function useCancelInvite(communityId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (inviteId: string) => cancelInvite(communityId, inviteId),
    onSuccess: () => {
      toast.success("Invite cancelled successfully")
      queryClient.invalidateQueries({ queryKey: ["pending-invites", communityId] })
      // Invalidate all community members queries (both regular and infinite)
      queryClient.invalidateQueries({ queryKey: ["community-members", communityId] })
      queryClient.invalidateQueries({ queryKey: ["communityMembers", communityId] })
      queryClient.invalidateQueries({ queryKey: ["communityRitualists", communityId] })
      // Invalidate eligible users so the list is refreshed
      queryClient.invalidateQueries({ queryKey: ["eligible-users", communityId] })
      queryClient.invalidateQueries({ queryKey: ["profileStatistics"] })
      queryClient.invalidateQueries({ queryKey: ["publicProfileStatistics"] })
    },
    onError: () => {
      toast.error("Failed to cancel invite")
    },
  })
}

export function useResendInvite(communityId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (inviteId: string) => resendInvite(communityId, inviteId),
    onSuccess: () => {
      toast.success("Invite resent successfully")
      queryClient.invalidateQueries({ queryKey: ["pending-invites", communityId] })
    },
    onError: () => {
      toast.error("Failed to resend invite")
    },
  })
}

export function useAcceptInvite(communityId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (inviteId: string) => acceptInvite(communityId, inviteId),
    onSuccess: () => {
      toast.success("Invitation accepted!")
      // Invalidate all community members queries (both regular and infinite)
      queryClient.invalidateQueries({ queryKey: ["community-members", communityId] })
      queryClient.invalidateQueries({ queryKey: ["communityMembers", communityId] })
      queryClient.invalidateQueries({ queryKey: ["communityRitualists", communityId] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      queryClient.invalidateQueries({ queryKey: ["profileStatistics"] })
      queryClient.invalidateQueries({ queryKey: ["publicProfileStatistics"] })
    },
    onError: () => {
      toast.error("Failed to accept invitation")
    },
  })

  return {
    acceptInvite: mutation.mutate,
    acceptInviteAsync: mutation.mutateAsync,
    isAccepting: mutation.isPending,
  }
}

export function useRejectInvite(communityId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (inviteId: string) => rejectInvite(communityId, inviteId),
    onSuccess: () => {
      toast.success("Invitation declined")
      // Invalidate all community members queries (both regular and infinite)
      queryClient.invalidateQueries({ queryKey: ["community-members", communityId] })
      queryClient.invalidateQueries({ queryKey: ["communityMembers", communityId] })
      queryClient.invalidateQueries({ queryKey: ["communityRitualists", communityId] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      queryClient.invalidateQueries({ queryKey: ["profileStatistics"] })
      queryClient.invalidateQueries({ queryKey: ["publicProfileStatistics"] })
    },
    onError: () => {
      toast.error("Failed to decline invitation")
    },
  })

  return {
    rejectInvite: mutation.mutate,
    rejectInviteAsync: mutation.mutateAsync,
    isRejecting: mutation.isPending,
  }
}
